import { useState, useRef, useEffect } from "react";
import CitaSelectorHora from "../cita/CitaSelectorHora";
import { mezclarColorAgenda } from "../../constants/agendaColores";
import { fechaISO, pad, formatCitaPreview } from "../../utils/noDispFechaHora";

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const HORAS_RAPIDAS = [9, 10, 11, 12, 13, 16, 17, 18];
const MIN_RAPIDOS = [0, 10, 15, 30, 45];

function pickTimeFromField(cur, fallbackH, fallbackM) {
  if (cur.includes("T")) {
    const parts = cur.split("T")[1].split(":");
    return `${pad(parseInt(parts[0], 10) || fallbackH)}:${pad(parseInt(parts[1], 10) || fallbackM)}`;
  }
  return `${pad(fallbackH)}:${pad(fallbackM)}`;
}

export default function NoDispFechaHoraPicker({
  dtInicio,
  dtFin,
  todoElDia,
  onChange,
  c,
  isDark,
  esFestivo,
  readOnly = false,
}) {
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [tcTarget, setTcTarget] = useState("inicio");
  const calAwaitFinRef = useRef(false);

  useEffect(() => {
    const raw = dtInicio || dtFin || "";
    if (raw.length >= 10) {
      const d = new Date(raw.slice(0, 10) + "T12:00:00");
      if (!Number.isNaN(d.getTime())) {
        setCalYear(d.getFullYear());
        setCalMonth(d.getMonth());
      }
    }
  }, [dtInicio, dtFin]);

  function handleDayClick(dateStr) {
    if (readOnly) return;
    if (tcTarget === "fin") {
      calAwaitFinRef.current = false;
      const timePart = pickTimeFromField(dtFin || "", 18, 0);
      onChange({ dt_fin: `${dateStr}T${timePart}` });
      return;
    }
    if (!calAwaitFinRef.current) {
      calAwaitFinRef.current = true;
      const timePart = pickTimeFromField(dtInicio || "", 9, 0);
      onChange({ dt_inicio: `${dateStr}T${timePart}` });
      return;
    }
    calAwaitFinRef.current = false;
    setTcTarget("fin");
    const tp = (dtFin || "").includes("T")
      ? pickTimeFromField(dtFin, 18, 0)
      : pickTimeFromField(dtInicio || "", 9, 30);
    onChange({ dt_fin: `${dateStr}T${tp}` });
  }

  function applyHM(h, m) {
    if (readOnly) return;
    const key = tcTarget === "inicio" ? "dt_inicio" : "dt_fin";
    const cur = key === "dt_inicio" ? dtInicio : dtFin;
    let d = (cur || "").slice(0, 10);
    if (!d) d = fechaISO(new Date());
    onChange({ [key]: `${d}T${pad(h)}:${pad(m)}` });
  }

  function applyQuickHour(h) {
    if (readOnly) return;
    const key = tcTarget === "inicio" ? "dt_inicio" : "dt_fin";
    const cur = key === "dt_inicio" ? dtInicio : dtFin;
    let d = (cur || "").slice(0, 10);
    let m = 0;
    if ((cur || "").includes("T")) {
      const pr = cur.split("T")[1].split(":");
      m = parseInt(pr[1], 10);
      if (!Number.isFinite(m)) m = 0;
    }
    if (!d) d = fechaISO(new Date());
    onChange({ [key]: `${d}T${pad(h)}:${pad(m)}` });
  }

  function applyQuickMin(minutes) {
    if (readOnly) return;
    const key = tcTarget === "inicio" ? "dt_inicio" : "dt_fin";
    const cur = key === "dt_inicio" ? dtInicio : dtFin;
    let d = (cur || "").slice(0, 10);
    let ho = 9;
    if ((cur || "").includes("T")) {
      const pr = cur.split("T")[1].split(":");
      ho = parseInt(pr[0], 10);
      if (!Number.isFinite(ho)) ho = 9;
    }
    if (!d) d = fechaISO(new Date());
    onChange({ [key]: `${d}T${pad(ho)}:${pad(minutes)}` });
  }

  function renderCalendarDays() {
    const startDate = (dtInicio || "").length >= 10 ? dtInicio.slice(0, 10) : "";
    const endDate = (dtFin || "").length >= 10 ? dtFin.slice(0, 10) : "";
    const lastDay = new Date(calYear, calMonth + 1, 0);
    const startDow = (new Date(calYear, calMonth, 1).getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let day = 1; day <= lastDay.getDate(); day++) cells.push(new Date(calYear, calMonth, day));

    return cells.map((date, i) => {
      if (!date) return <div key={`ce${i}`} style={{ height: 30 }} />;
      const dStr = fechaISO(date);
      const isStart = startDate && dStr === startDate;
      const isEnd = endDate && dStr === endDate;
      const isFest = esFestivo?.(dStr);
      const isSunday = date.getDay() === 0;
      const isToday = dStr === fechaISO(new Date());

      let circBg = "transparent";
      if (isStart && isEnd) circBg = "#f59e0b";
      else if (isStart) circBg = "#f59e0b";
      else if (isEnd) circBg = "#0284c7";
      else if (isToday) circBg = isDark ? mezclarColorAgenda("#3b82f6", "#1e293b", 0.45) : "#e8f0f8";

      const ring = isStart && isEnd;
      const circBorder = ring
        ? `2px solid ${isDark ? "#fcd34d" : "#fff8e1"}`
        : isToday && !isStart && !isEnd
          ? `2px solid ${isDark ? "#94a3b8" : "#1a3a5c"}`
          : "none";
      const textColor = isStart || isEnd ? "#ffffff" : isFest || isSunday ? "#b91c1c" : c.textPrimary;

      return (
        <div key={i} onClick={() => handleDayClick(dStr)} style={{ height: 30, display: "flex", alignItems: "center", justifyContent: "center", background: "transparent", cursor: "pointer" }}>
          <div style={{ width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: circBg, border: circBorder, fontSize: 11, fontWeight: isStart || isEnd ? 700 : 500, color: textColor, userSelect: "none" }}>
            {date.getDate()}
          </div>
        </div>
      );
    });
  }

  const activeDt = tcTarget === "inicio" ? dtInicio : dtFin;
  let selH = 9;
  let selM = 0;
  if ((activeDt || "").includes("T")) {
    const pr = activeDt.split("T")[1].split(":");
    selH = parseInt(pr[0], 10);
    selM = parseInt(pr[1], 10);
    if (!Number.isFinite(selH)) selH = 9;
    if (!Number.isFinite(selM)) selM = 0;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, opacity: readOnly ? 0.6 : 1, pointerEvents: readOnly ? "none" : "auto" }} title={readOnly ? "No tienes permiso para modificar la fecha/hora" : undefined}>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          disabled={readOnly}
          onClick={() => { calAwaitFinRef.current = false; setTcTarget("inicio"); }}
          style={{ flex: 1, padding: "6px 8px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600, border: `2px solid ${tcTarget === "inicio" ? "#0369a1" : c.sectionBorder}`, background: tcTarget === "inicio" ? (isDark ? "#0369a133" : "#e0f2fe") : c.bgInput, color: c.textPrimary }}
        >
          Desde
        </button>
        <button
          type="button"
          disabled={readOnly}
          onClick={() => { calAwaitFinRef.current = false; setTcTarget("fin"); }}
          style={{ flex: 1, padding: "6px 8px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600, border: `2px solid ${tcTarget === "fin" ? "#0369a1" : c.sectionBorder}`, background: tcTarget === "fin" ? (isDark ? "#0369a133" : "#e0f2fe") : c.bgInput, color: c.textPrimary }}
        >
          Hasta
        </button>
      </div>
      <div style={{ fontSize: 11, color: c.textMuted, lineHeight: 1.35 }}>
        <span style={{ color: c.textSecondary }}>Desde:</span> {formatCitaPreview(dtInicio)}
        <span style={{ margin: "0 6px", color: c.textMuted }}>·</span>
        <span style={{ color: c.textSecondary }}>Hasta:</span> {formatCitaPreview(dtFin)}
      </div>
      <div style={{ border: `1px solid ${c.sectionBorder}`, borderRadius: 10, overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", background: isDark ? c.bgInput : "#f9f6ef", borderBottom: `1px solid ${c.sectionBorder}` }}>
          <button type="button" onClick={() => { if (calMonth === 0) { setCalYear((y) => y - 1); setCalMonth(11); } else setCalMonth((m) => m - 1); }} style={{ background: "none", border: "none", color: c.textPrimary, fontSize: 15, cursor: "pointer", lineHeight: 1, padding: "0 6px" }}>‹</button>
          <span style={{ fontSize: 12, fontWeight: 700, color: c.textPrimary }}>{MESES[calMonth]} {calYear}</span>
          <button type="button" onClick={() => { if (calMonth === 11) { setCalYear((y) => y + 1); setCalMonth(0); } else setCalMonth((m) => m + 1); }} style={{ background: "none", border: "none", color: c.textPrimary, fontSize: 15, cursor: "pointer", lineHeight: 1, padding: "0 6px" }}>›</button>
        </div>
        <div style={{ padding: "0 4px 4px", background: isDark ? c.bgCard : "#ffffff" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", background: isDark ? c.bgPanel : "#f4f1e8", padding: "2px 4px 0" }}>
            {["L", "M", "X", "J", "V", "S", "D"].map((d, di) => (
              <div key={d} style={{ textAlign: "center", fontSize: 9, fontWeight: 700, color: di === 6 ? "#b91c1c" : c.textMuted, padding: "2px 0" }}>{d}</div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", padding: "0 2px 4px" }}>
            {renderCalendarDays()}
          </div>
        </div>
      </div>
      {!todoElDia && (
        <>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: c.textSecondary, marginBottom: 4 }}>
              Hora {tcTarget === "inicio" ? "inicio" : "fin"}
            </div>
            <CitaSelectorHora
              value={`${pad(selH)}:${pad(selM)}`}
              onChange={(v) => {
                const [h, m] = v.split(":").map((x) => parseInt(x, 10));
                applyHM(h, m);
              }}
              c={c}
              isDark={isDark}
              accentColor="#dc2626"
              accentBg={isDark ? "#dc262633" : "#fee2e2"}
            />
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <div style={{ flexShrink: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: c.textSecondary, marginBottom: 4 }}>Horas rápidas</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {HORAS_RAPIDAS.map((h) => {
                  const cur = tcTarget === "inicio" ? dtInicio : dtFin;
                  let ch = null;
                  if ((cur || "").includes("T")) ch = parseInt(cur.split("T")[1].split(":")[0], 10);
                  const active = ch === h;
                  return (
                    <button key={`r${h}`} type="button" onClick={() => applyQuickHour(h)} style={{ padding: "4px 9px", borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: "pointer", border: `1px solid ${active ? "#0369a1" : c.sectionBorder}`, background: active ? (isDark ? "#0369a144" : "#bae6fd") : c.bgInput, color: c.textPrimary }}>{h}:00</button>
                  );
                })}
              </div>
            </div>
            <div style={{ flexShrink: 0 }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: c.textSecondary, marginBottom: 4 }}>Minutos rápidos</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {MIN_RAPIDOS.map((m) => {
                  const cur = tcTarget === "inicio" ? dtInicio : dtFin;
                  let cm = null;
                  if ((cur || "").includes("T")) cm = parseInt(cur.split("T")[1].split(":")[1], 10);
                  const active = cm === m;
                  const label = m === 0 ? ":00" : `:${pad(m)}`;
                  return (
                    <button key={m} type="button" onClick={() => applyQuickMin(m)} style={{ padding: "4px 10px", borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: "pointer", border: `1px solid ${active ? "#0369a1" : c.sectionBorder}`, background: active ? (isDark ? "#0369a144" : "#bae6fd") : c.bgInput, color: c.textPrimary }}>{label}</button>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
